import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

export const NOTIFICATION_RECIPIENTS_KEY = ['notificationRecipients']

export interface RecipientList {
  key: string
  label: string
  description: string
  /** Deal types that trigger this list. Empty means every type. */
  dealTypes: string[]
  /** The config value, shown as a reference alongside the current membership. */
  defaults: string[]
  userIds: string[]
  /** True when nothing has been saved and the config default is in use. */
  usingDefaults: boolean
  updatedAt: string | null
  updatedBy: string | null
}

/**
 * The deal-notification lists an admin can edit. Admin-gated server side, so
 * `userId` is sent to identify the caller. Not cached: an admin opening this
 * wants to see the live state, not a stale copy.
 */
export const useNotificationRecipients = (userId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: [...NOTIFICATION_RECIPIENTS_KEY, userId],
    enabled: Boolean(enabled && userId),
    staleTime: 0,
    retry: 1,
    queryFn: async (): Promise<RecipientList[]> => {
      const { data } = await axios.get(`${baseUrl}/aws/notification-recipients`, { params: { userId } })
      return Array.isArray(data?.lists) ? data.lists : []
    },
  })
}

export const useSaveNotificationRecipients = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      lists,
      currentUser,
    }: {
      lists: Array<{ key: string; userIds: string[] }>
      currentUser: { user_uuid?: string }
    }): Promise<void> => {
      // One request per list, so a failure on one leaves the others saved and
      // the error names which list did not take.
      for (const list of lists) {
        await axios.post(`${baseUrl}/aws/notification-recipients/${list.key}`, {
          userIds: list.userIds,
          currentUser,
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_RECIPIENTS_KEY })
    },
  })
}
